import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, XCircle, RefreshCw, UploadCloud, Boxes,
  Copy, RotateCcw, AlertTriangle, PlayCircle,
} from "lucide-react";

const Dot = ({ ok }) => ok
  ? <CheckCircle2 className="w-4 h-4 text-green-600" />
  : <XCircle className="w-4 h-4 text-red-500" />;

const Stat = ({ label, value, testid }) => (
  <div className="border border-black/10 bg-white px-3 py-2.5" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-xl font-black font-display">{value ?? 0}</p>
  </div>
);

const Toggle = ({ label, name, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 border border-black/10 bg-white px-3 py-2.5 text-sm cursor-pointer" data-testid={testid}>
    <span>{label}</span>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(name, e.target.checked)} className="w-4 h-4 accent-taven-orange" />
  </label>
);

const Bar = ({ label, value, total, color }) => (
  <div className="mb-1.5">
    <div className="flex justify-between text-xs mb-0.5"><span>{label}</span><span className="font-bold">{value}</span></div>
    <div className="h-2 bg-black/5 rounded overflow-hidden"><div className={color} style={{ width: `${total ? (value / total) * 100 : 0}%`, height: "100%" }} /></div>
  </div>
);

const BrainPanel = () => {
  const [intel, setIntel] = useState(null);
  const load = () => adminClient().get("/admin/cj/intelligence").then(({ data }) => setIntel(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setMode = async (mode) => {
    try { await adminClient().post(`/admin/cj/mode/${mode}`); toast.success(`Switched to ${mode} mode.`); load(); }
    catch { toast.error("Could not switch mode."); }
  };
  if (!intel) return null;
  const pd = intel.profit_distribution || {};
  const qd = intel.quality_distribution || {};
  const t = intel.analyzed || 1;
  const mode = intel.operating_mode;
  return (
    <div className="border border-black/10 bg-taven-gray/30 p-4 mb-6" data-testid="automation-brain">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Automation Brain — decision engine over {intel.analyzed} active products</p>
        <div className="flex items-center gap-1 border border-black/10 bg-white p-0.5" data-testid="mode-switch">
          {["launch", "growth"].map((mkey) => (
            <button key={mkey} onClick={() => setMode(mkey)} data-testid={`mode-${mkey}`}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${mode === mkey ? "bg-taven-black text-white" : "text-muted-foreground hover:bg-taven-gray"}`}>{mkey}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="border border-black/10 bg-white p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Eligible to publish</p>
          <p className="text-2xl font-black font-display text-green-700" data-testid="brain-eligible">{intel.eligible_to_publish}</p>
          <p className="text-[11px] text-muted-foreground">Blocked by rules: {intel.blocked_from_publish} · Homepage-ready: {intel.eligible_for_homepage}</p>
        </div>
        <div className="border border-black/10 bg-white p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Profit Intelligence</p>
          <Bar label="High Profit" value={pd["High Profit"] || 0} total={t} color="bg-green-600" />
          <Bar label="Safe Profit" value={pd["Safe Profit"] || 0} total={t} color="bg-emerald-500" />
          <Bar label="Low Profit" value={pd["Low Profit"] || 0} total={t} color="bg-amber-500" />
          <Bar label="Risky" value={pd["Risky"] || 0} total={t} color="bg-orange-500" />
          <Bar label="Stop Selling" value={pd["Stop Selling"] || 0} total={t} color="bg-red-500" />
        </div>
        <div className="border border-black/10 bg-white p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Quality Score</p>
          <Bar label="Excellent (85+)" value={qd["Excellent"] || 0} total={t} color="bg-taven-gold" />
          <Bar label="Great (70+)" value={qd["Great"] || 0} total={t} color="bg-yellow-500" />
          <Bar label="Good (55+)" value={qd["Good"] || 0} total={t} color="bg-lime-500" />
          <Bar label="Fair (40+)" value={qd["Fair"] || 0} total={t} color="bg-amber-400" />
          <Bar label="Poor" value={qd["Poor"] || 0} total={t} color="bg-red-400" />
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Min quality to publish: <strong>{intel.min_quality_score}</strong> · Profit tiers: High ≥ ${intel.thresholds?.high} · Safe ≥ ${intel.thresholds?.safe} · Low ≥ ${intel.thresholds?.low}. Only eligible products auto-publish.</p>
    </div>
  );
};

const CollectionHealthPanel = () => {
  const [h, setH] = useState(null);
  const [filling, setFilling] = useState(false);
  const load = () => adminClient().get("/admin/cj/collection-health").then(({ data }) => setH(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const fill = async () => {
    setFilling(true);
    try { const { data } = await adminClient().post("/admin/cj/fill-collections"); toast.success(`Fill: ${JSON.stringify(data.totals || data)}`); load(); }
    catch { toast.error("Fill failed"); } finally { setFilling(false); }
  };
  if (!h) return null;
  const sum = h.summary || {};
  return (
    <div className="mb-6" data-testid="collection-health">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Collection Health — target {h.target}/collection</h2>
        <button onClick={fill} disabled={filling} className="flex items-center gap-1.5 bg-taven-black text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50" data-testid="fill-collections-btn">
          <Boxes className="w-3.5 h-3.5" /> {filling ? "Filling…" : "Auto-Fill Neediest"}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
        <Stat label="Complete" value={sum.complete} testid="ch-complete" />
        <Stat label="Healthy" value={sum.healthy} testid="ch-healthy" />
        <Stat label="Low" value={sum.low} testid="ch-low" />
        <Stat label="Empty" value={sum.empty} testid="ch-empty" />
        <Stat label="Avg Fill %" value={sum.avg_fill} testid="ch-avgfill" />
      </div>
      <div className="border border-black/10 bg-white p-3">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Neediest collections</p>
        {(h.needs_fill || []).slice(0, 8).map((r) => (
          <div key={r.handle} className="flex items-center gap-2 mb-1.5 text-sm" data-testid={`ch-row-${r.handle}`}>
            <span className="w-40 truncate">{r.name || r.handle}</span>
            <div className="flex-1 h-2 bg-black/5 rounded overflow-hidden"><div className="bg-taven-gold h-full" style={{ width: `${r.fill_pct}%` }} /></div>
            <span className="text-xs text-muted-foreground w-20 text-right">{r.count}/{r.target}</span>
          </div>
        ))}
        {(!h.needs_fill || h.needs_fill.length === 0) && <p className="text-sm text-muted-foreground">All collections are healthy.</p>}
      </div>
    </div>
  );
};

const SuppliersPanel = () => {
  const [d, setD] = useState(null);
  const [aliState, setAliState] = useState(null);
  const [aliBusy, setAliBusy] = useState(false);
  useEffect(() => {
    adminClient().get("/admin/suppliers").then(({ data }) => setD(data)).catch(() => {});
    adminClient().get("/admin/aliexpress/status")
      .then(({ data }) => setAliState(data)).catch(() => setAliState({ ui_state: "connect" }));
  }, []);
  const connectAli = async () => {
    if (aliBusy) return;
    setAliBusy(true);
    try {
      const { data } = await adminClient().get("/admin/aliexpress/oauth/start");
      if (data.ok && data.authorize_url) { window.location.href = data.authorize_url; return; }
      toast.error(data.error === "token_encryption_unavailable"
        ? "Set ALIEXPRESS_TOKEN_ENC_KEY in Preview .env, then retry."
        : (data.error || "Could not start AliExpress connection"));
    } catch { toast.error("Could not start AliExpress connection"); }
    finally { setAliBusy(false); }
  };
  if (!d) return null;
  const uiState = aliState?.ui_state || "connect";
  const renderAli = () => {
    if (uiState === "connected_managed") {
      return <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-green-700" data-testid="aliexpress-status-label">Connected — Automatically Managed</span>;
    }
    const label = uiState === "reauthorize" ? "Reauthorize" : "Connect";
    const cls = uiState === "reauthorize"
      ? "bg-amber-500 text-black hover:bg-amber-400"
      : "bg-black text-white hover:bg-taven-gold hover:text-black";
    return (
      <button onClick={connectAli} disabled={aliBusy}
        data-testid={uiState === "reauthorize" ? "aliexpress-reauthorize-btn" : "aliexpress-connect-btn"}
        className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full disabled:opacity-50 transition-colors ${cls}`}>
        {aliBusy ? "Opening…" : label}
      </button>
    );
  };
  return (
    <div className="mb-6" data-testid="suppliers-panel">
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Suppliers — one pipeline, {d.active} active / {d.suppliers.length} ready</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {d.suppliers.map((sup) => {
          const isAli = sup.key === "aliexpress";
          return (
            <div key={sup.key} className="border border-black/10 bg-white px-3 py-2.5 flex items-center justify-between gap-2" data-testid={`supplier-${sup.key}`}>
              <div>
                <p className="font-bold text-sm">{sup.name}</p>
                <p className="text-[11px] text-muted-foreground">{sup.products} products · {sup.capabilities.join(", ")}</p>
              </div>
              {isAli && sup.configured ? renderAli() : (
                <span className={`text-[10px] font-bold uppercase tracking-wider ${sup.status === "active" ? "text-green-700" : sup.status === "ready" ? "text-amber-600" : "text-muted-foreground"}`}>{sup.status.replace(/_/g, " ")}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OrderSyncPanel = () => {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const loadBudget = () => adminClient().get("/admin/cj/budget").then(({ data }) => setB(data)).catch(() => {});
  useEffect(() => { loadBudget(); }, []);

  const sync = async () => {
    if (!window.confirm("Run a one-time CJ order sync now?\n\nThis polls CJ for fulfillment/tracking on placed orders only. It will NOT import products or inventory, will stop immediately on a CJ quota error, and will not retry.")) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await adminClient().post("/admin/cj/sync-orders");
      setResult(data.result);
      loadBudget();
      const r = data.result || {};
      if (r.stopped_on_quota) toast.error(`Stopped on CJ quota — synced ${r.synced || 0}/${r.found || 0} before halting.`);
      else toast.success(`Order sync done — found ${r.found || 0}, synced ${r.synced || 0}, skipped ${r.skipped || 0}, failed ${r.failed || 0}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Order sync failed";
      toast.error(msg);
      setResult({ error: msg });
    } finally { setBusy(false); }
  };

  const budgetExhausted = b && (b.quota_error || b.points_used >= b.daily_budget);

  return (
    <div className="mb-6" data-testid="order-sync-panel">
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Order Fulfillment</h2>
      <div className="border border-black/10 bg-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-bold">Sync CJ Orders Now</p>
            <p className="text-[11px] text-muted-foreground max-w-xl mt-0.5">Manual, one execution. Polls CJ for tracking/fulfillment on placed orders only — no catalog, inventory or product imports. Stops immediately on CJ quota error 16900500, no automatic retry. Fully audited.</p>
          </div>
          <button onClick={sync} disabled={busy || budgetExhausted} data-testid="sync-orders-now-btn"
            className="flex items-center gap-1.5 bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Sync CJ Orders Now"}
          </button>
        </div>
        {b && (
          <p className="text-[11px] text-muted-foreground mt-3" data-testid="order-sync-budget">
            Daily CJ point budget: <strong>{Number(b.points_used).toLocaleString()}</strong> / {Number(b.daily_budget).toLocaleString()} used · Live calls: <strong>{b.live_calls_enabled ? "ENABLED" : "DISABLED"}</strong>{b.quota_error ? " · quota error today" : ""}
            {budgetExhausted ? " — budget exhausted, try again after reset." : ""}
          </p>
        )}
        {result && !result.error && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3" data-testid="order-sync-result">
            <Stat label="Found" value={result.found} testid="os-found" />
            <Stat label="Synced" value={result.synced} testid="os-synced" />
            <Stat label="Skipped" value={result.skipped} testid="os-skipped" />
            <Stat label="Failed" value={result.failed} testid="os-failed" />
            <Stat label="Quota Stop" value={result.stopped_on_quota ? "Yes" : "No"} testid="os-quota" />
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminCJAutomation() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => adminClient().get("/admin/cj/monitor").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const run = async (label, fn) => {
    setBusy(label);
    try { const { data } = await fn(); toast.success(`${label}: ${JSON.stringify(data)}`); load(); }
    catch (e) { toast.error(`${label} failed`); } finally { setBusy(""); }
  };

  const setSetting = async (name, value) => {
    try {
      await adminClient().put("/admin/cj/settings", { [name]: value });
      setData((d) => ({ ...d, settings: { ...d.settings, [name]: value } }));
      toast.success("Automation setting saved.");
    } catch { toast.error("Could not save setting."); }
  };

  const setInterval = async (name, value) => {
    const v = Math.max(5, Math.min(1440, Number(value) || 5));
    await setSetting(name, v);
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading automation monitor…</p>;
  if (data.error) return <p className="text-sm text-red-600">Could not load the automation monitor.</p>;

  const s = data.settings || {};
  const c = data.catalog || {};
  const p = data.pipeline || {};
  const gated = !data.shopify_admin_configured;

  return (
    <div data-testid="admin-cj-automation">
      <div className="flex items-center gap-2 mb-1"><Activity className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">CJ Automation & Monitoring</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Fully automated sourcing → approval → publishing → inventory → fulfillment. Supplier identity is never exposed to the storefront. Shopify writes stay gated until the Admin API token is connected.</p>

      {/* Readiness */}
      <div className="grid sm:grid-cols-3 gap-2 mb-5">
        <div className="flex items-center gap-2 border border-black/10 bg-white px-3 py-2.5 text-sm" data-testid="cj-ready-shopify"><Dot ok={data.shopify_admin_configured} /> Shopify Admin token</div>
        <div className="flex items-center gap-2 border border-black/10 bg-white px-3 py-2.5 text-sm" data-testid="cj-ready-webhook"><Dot ok={data.webhook_secret_configured} /> Webhook secret</div>
        <div className="flex items-center gap-2 border border-black/10 bg-white px-3 py-2.5 text-sm" data-testid="cj-ready-cj"><Dot ok={data.cj_token_configured} /> CJ access token</div>
      </div>

      {gated && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-4 mb-5 flex items-start gap-2" data-testid="cj-gated-notice">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Publishing, live fulfillment write-back and inventory flips to Shopify are <strong>gated</strong> until <code>SHOPIFY_ADMIN_API_TOKEN</code> is added. Everything else (sourcing, approval, dedupe, local inventory/archive, retry queue) runs now.</span>
        </div>
      )}

      <BrainPanel />

      {/* Operations */}
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Operations — Today</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="operations-panel">
        <Stat label="Imported Today" value={data.operations?.imported_today} testid="op-imported" />
        <Stat label="Published Today" value={data.operations?.published_today} testid="op-published" />
        <Stat label="Rejected Today" value={data.operations?.rejected_today} testid="op-rejected" />
        <Stat label="Collections Complete" value={data.operations?.collections_completed} testid="op-collections" />
        <Stat label="Inventory Health %" value={data.operations?.inventory_health_pct} testid="op-health" />
        <Stat label="Avg Profit $" value={data.operations?.average_profit} testid="op-profit" />
        <Stat label="Orders Processed" value={data.operations?.orders_processed} testid="op-orders" />
        <Stat label="Suppliers Active" value={`${data.operations?.suppliers_active}/${data.operations?.suppliers_total}`} testid="op-suppliers" />
      </div>

      <CollectionHealthPanel />
      <SuppliersPanel />

      <OrderSyncPanel />

      {/* Catalog */}
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Catalog</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        <Stat label="Active" value={c.by_status?.active} testid="cj-stat-active" />
        <Stat label="Pending Approval" value={c.by_status?.pending_approval} testid="cj-stat-pending-approval" />
        <Stat label="Pending Publish" value={c.pending_publish} testid="cj-stat-pending-publish" />
        <Stat label="Published→Shopify" value={c.published_to_shopify} testid="cj-stat-published" />
        <Stat label="Archived" value={c.archived} testid="cj-stat-archived" />
        <Stat label="Review Queue" value={p.review_queue} testid="cj-stat-review" />
        <Stat label="Orders Awaiting" value={p.orders_awaiting_fulfillment} testid="cj-stat-awaiting" />
        <Stat label="Orders Shipped" value={p.orders_shipped} testid="cj-stat-shipped" />
      </div>

      {/* Error recovery */}
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Error Recovery</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        <Stat label="Retry Queued" value={p.dead_letter_queued} testid="cj-stat-dl-queued" />
        <Stat label="Failed (gave up)" value={p.dead_letter_failed} testid="cj-stat-dl-failed" />
      </div>

      {/* Controls */}
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Automation Settings</h2>
      <div className="grid sm:grid-cols-2 gap-2 mb-3">
        <Toggle label="Emergency pause (halt all automation)" name="emergency_pause" checked={s.emergency_pause} onChange={setSetting} testid="cj-toggle-pause" />
        <Toggle label="Auto-publish approved products" name="publish_enabled" checked={s.publish_enabled} onChange={setSetting} testid="cj-toggle-publish" />
        <Toggle label="Publish as ACTIVE (else DRAFT for review)" name="draft_to_active" checked={s.draft_to_active} onChange={setSetting} testid="cj-toggle-active" />
        <Toggle label="Auto-archive when CJ stock hits zero" name="auto_archive_on_zero" checked={s.auto_archive_on_zero} onChange={setSetting} testid="cj-toggle-archive" />
        <Toggle label="Auto-retry failed jobs" name="retry_enabled" checked={s.retry_enabled} onChange={setSetting} testid="cj-toggle-retry" />
      </div>
      <div className="flex flex-wrap gap-4 mb-6">
        <label className="text-sm">Publish every
          <input type="number" defaultValue={s.publish_interval_minutes} onBlur={(e) => setInterval("publish_interval_minutes", e.target.value)}
            className="w-20 border border-black/15 px-2 py-1.5 text-sm ml-2 outline-none focus:border-taven-gold" data-testid="cj-publish-interval" /> min
        </label>
        <label className="text-sm">Inventory sync every
          <input type="number" defaultValue={s.inventory_sync_interval_minutes} onBlur={(e) => setInterval("inventory_sync_interval_minutes", e.target.value)}
            className="w-20 border border-black/15 px-2 py-1.5 text-sm ml-2 outline-none focus:border-taven-gold" data-testid="cj-inventory-interval" /> min
        </label>
        <label className="text-sm">Products / publish batch
          <select defaultValue={s.publish_batch_size} onChange={(e) => setSetting("publish_batch_size", Number(e.target.value))}
            className="border border-black/15 px-2 py-1.5 text-sm ml-2 outline-none focus:border-taven-gold" data-testid="cj-batch-size">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="text-sm">Max products / day
          <input type="number" defaultValue={s.max_products_per_day} onBlur={(e) => setSetting("max_products_per_day", Math.max(0, Number(e.target.value) || 0))}
            className="w-24 border border-black/15 px-2 py-1.5 text-sm ml-2 outline-none focus:border-taven-gold" data-testid="cj-max-day" /> <span className="text-[11px] text-muted-foreground">0 = ∞</span>
        </label>
        <label className="text-sm">Target / collection
          <input type="number" defaultValue={s.products_per_collection_target} onBlur={(e) => setSetting("products_per_collection_target", Math.max(1, Number(e.target.value) || 200))}
            className="w-20 border border-black/15 px-2 py-1.5 text-sm ml-2 outline-none focus:border-taven-gold" data-testid="cj-target" />
        </label>
      </div>

      {/* Manual triggers */}
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Run Now</h2>
      <div className="flex flex-wrap gap-2 mb-6">
        <button disabled={busy} onClick={() => run("Publish batch", () => adminClient().post("/admin/cj/publish-batch?force=true"))} className="flex items-center gap-1.5 bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50" data-testid="cj-run-publish"><UploadCloud className="w-4 h-4" /> Publish Batch</button>
        <button disabled={busy} onClick={() => run("Inventory sync", () => adminClient().post("/admin/cj/inventory-sync"))} className="flex items-center gap-1.5 border border-black/15 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray disabled:opacity-50" data-testid="cj-run-inventory"><Boxes className="w-4 h-4" /> Inventory Sync</button>
        <button disabled={busy} onClick={() => run("Dedupe", () => adminClient().post("/admin/cj/dedupe"))} className="flex items-center gap-1.5 border border-black/15 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray disabled:opacity-50" data-testid="cj-run-dedupe"><Copy className="w-4 h-4" /> Dedupe</button>
        <button disabled={busy} onClick={() => run("Retry", () => adminClient().post("/admin/cj/dead-letter/retry"))} className="flex items-center gap-1.5 border border-black/15 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray disabled:opacity-50" data-testid="cj-run-retry"><RotateCcw className="w-4 h-4" /> Retry Failed</button>
        <button disabled={busy} onClick={() => run("Backup", () => adminClient().post("/admin/cj/backup"))} className="flex items-center gap-1.5 border border-black/15 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray disabled:opacity-50" data-testid="cj-run-backup"><RotateCcw className="w-4 h-4" /> Backup Now</button>
        <button disabled={busy} onClick={() => run("Shopify connection", () => adminClient().get("/admin/cj/shopify-connection-test"))} className="flex items-center gap-1.5 border border-black/15 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray disabled:opacity-50" data-testid="cj-test-shopify"><Activity className="w-4 h-4" /> Test Shopify Connection</button>
        <button disabled={busy} onClick={load} className="flex items-center gap-1.5 border border-black/15 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray disabled:opacity-50" data-testid="cj-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {/* Activity log */}
      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><PlayCircle className="w-4 h-4" /> Recent Activity</h2>
      <div className="border border-black/10 bg-white overflow-x-auto" data-testid="cj-activity-log">
        <table className="w-full text-sm">
          <thead className="bg-taven-gray/60 text-left"><tr><th className="px-3 py-2 font-bold">When</th><th className="px-3 py-2 font-bold">Event</th><th className="px-3 py-2 font-bold">Details</th></tr></thead>
          <tbody className="divide-y divide-black/5">
            {(data.recent_activity || []).map((a, i) => (
              <tr key={i}>
                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{a.at ? new Date(a.at).toLocaleString() : ""}</td>
                <td className="px-3 py-2 font-semibold">{a.type}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{Object.entries(a).filter(([k]) => !["type", "at", "id"].includes(k)).map(([k, v]) => `${k}=${v}`).join(", ")}</td>
              </tr>
            ))}
            {(!data.recent_activity || data.recent_activity.length === 0) && <tr><td colSpan={3} className="px-3 py-4 text-center text-muted-foreground">No activity yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
